import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Smile, BookOpen, Target, Gauge, Activity, TrendingUp, Clock, Users, ArrowUpDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

export const StatisticsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  const [overview, setOverview] = useState(null);
  const [sessionRatings, setSessionRatings] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendancePeriod, setAttendancePeriod] = useState("month");
  const [attendanceSort, setAttendanceSort] = useState("high");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;
      try {
        const [overviewRes, ratingsRes] = await Promise.all([
          axios.get(`${API}/stats/overview?user_id=${user.id}`),
          axios.get(`${API}/stats/session-ratings?limit=10&user_id=${user.id}`)
        ]);
        setOverview(overviewRes.data);
        setSessionRatings(ratingsRes.data);
      } catch (e) {
        console.error("Error fetching stats:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user?.id]);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!user?.id || !isCoach) return;
      setAttendanceLoading(true);
      try {
        const response = await axios.get(`${API}/coach/team/attendance?coach_id=${user.id}&period=${attendancePeriod}`);
        setAttendanceData(response.data.participants || []);
      } catch (e) {
        console.error("Error fetching attendance:", e);
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchAttendance();
  }, [user?.id, isCoach, attendancePeriod]);

  const chartColors = { fun: '#eab308', learning: '#3b82f6', mygoal: '#8b5cf6', intensity: '#ef4444' };

  const attendancePeriodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'season', label: 'This Season' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];

  const sortedAttendanceData = [...attendanceData].sort((a, b) => {
    if (attendanceSort === 'low') return a.attendance_percentage - b.attendance_percentage;
    if (attendanceSort === 'name') return a.athlete_name.localeCompare(b.athlete_name);
    return b.attendance_percentage - a.attendance_percentage;
  });

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-statistics">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Statistics</h1>
        <div className="w-10" />
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
              <h3 className={`font-semibold mb-4 ${theme.textPrimary}`}>Training Rating Averages</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3">
                  <Smile className="w-6 h-6 text-yellow-500" />
                  <div><p className={`text-xs ${theme.textMuted}`}>Fun</p><p className={`text-xl font-bold ${theme.textPrimary}`}>{overview?.avg_fun?.toFixed(1) || "-"}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-blue-500" />
                  <div><p className={`text-xs ${theme.textMuted}`}>Learning</p><p className={`text-xl font-bold ${theme.textPrimary}`}>{overview?.avg_learning?.toFixed(1) || "-"}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Target className="w-6 h-6 text-purple-500" />
                  <div><p className={`text-xs ${theme.textMuted}`}>MyGoal</p><p className={`text-xl font-bold ${theme.textPrimary}`}>{overview?.avg_mygoal?.toFixed(1) || "-"}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Gauge className="w-6 h-6 text-red-500" />
                  <div><p className={`text-xs ${theme.textMuted}`}>Intensity</p><p className={`text-xl font-bold ${theme.textPrimary}`}>{overview?.avg_intensity?.toFixed(1) || "-"}</p></div>
                </div>
              </div>
            </div>

            {sessionRatings.length > 0 && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <h3 className={`font-semibold mb-4 ${theme.textPrimary}`}>Training Ratings Over Time</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={sessionRatings}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                    <XAxis dataKey="date" stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(73,57,134,0.5)'} fontSize={10} />
                    <YAxis domain={[0, 10]} stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(73,57,134,0.5)'} fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1a2e' : '#fff', border: 'none', borderRadius: '8px' }} />
                    <Line type="monotone" dataKey="fun" stroke={chartColors.fun} strokeWidth={2} dot={{ r: 4 }} name="Fun" />
                    <Line type="monotone" dataKey="learning" stroke={chartColors.learning} strokeWidth={2} dot={{ r: 4 }} name="Learning" />
                    <Line type="monotone" dataKey="mygoal" stroke={chartColors.mygoal} strokeWidth={2} dot={{ r: 4 }} name="MyGoal" />
                    <Line type="monotone" dataKey="intensity" stroke={chartColors.intensity} strokeWidth={2} dot={{ r: 4 }} name="Intensity" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-yellow-500" /><span className={`text-xs ${theme.textMuted}`}>Fun</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className={`text-xs ${theme.textMuted}`}>Learning</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-purple-500" /><span className={`text-xs ${theme.textMuted}`}>MyGoal</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500" /><span className={`text-xs ${theme.textMuted}`}>Intensity</span></div>
                </div>
              </div>
            )}

            {isCoach && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`} data-testid="coach-attendance-card">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Users className={`w-5 h-5 ${theme.textSecondary}`} />
                    <h3 className={`font-semibold ${theme.textPrimary}`} data-testid="coach-attendance-title">Attendance per Athlete</h3>
                  </div>
                  <ArrowUpDown className={`w-4 h-4 ${theme.textMuted}`} />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className={`text-xs ${theme.textMuted} mb-1 block`}>Period</label>
                    <select
                      data-testid="coach-attendance-period-select"
                      value={attendancePeriod}
                      onChange={(e) => setAttendancePeriod(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`}
                      style={{ color: isDark ? '#fff' : '#1a1a2e' }}
                    >
                      {attendancePeriodOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`text-xs ${theme.textMuted} mb-1 block`}>Sort</label>
                    <select
                      data-testid="coach-attendance-sort-select"
                      value={attendanceSort}
                      onChange={(e) => setAttendanceSort(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl border text-sm ${theme.input}`}
                      style={{ color: isDark ? '#fff' : '#1a1a2e' }}
                    >
                      <option value="high">Highest %</option>
                      <option value="low">Lowest %</option>
                      <option value="name">Name A-Z</option>
                    </select>
                  </div>
                </div>

                {attendanceLoading ? (
                  <p className={`text-sm ${theme.textMuted}`} data-testid="coach-attendance-loading">Loading attendance...</p>
                ) : sortedAttendanceData.length === 0 ? (
                  <p className={`text-sm ${theme.textMuted}`} data-testid="coach-attendance-empty">No athlete attendance data available for this period.</p>
                ) : (
                  <div className="space-y-2" data-testid="coach-attendance-list">
                    {sortedAttendanceData.map((row) => (
                      <div
                        key={row.athlete_id}
                        data-testid={`coach-attendance-row-${row.athlete_id}`}
                        className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'} flex items-center justify-between gap-3`}
                      >
                        <p className={`text-sm font-medium ${theme.textPrimary}`}>{row.athlete_name}</p>
                        <p className={`text-sm ${theme.textSecondary}`} data-testid={`coach-attendance-value-${row.athlete_id}`}>
                          {row.attendance_percentage.toFixed(1)}% ({row.rated_sessions}/{row.assigned_sessions})
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            <div className="grid grid-cols-2 gap-3">
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <div className="flex items-center gap-2 mb-2"><Activity className={`w-5 h-5 ${theme.textSecondary}`} /><span className={`text-sm ${theme.textSecondary}`}>Sessions</span></div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{overview?.total_sessions || 0}</p>
              </div>
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <div className="flex items-center gap-2 mb-2"><Target className={`w-5 h-5 ${theme.textSecondary}`} /><span className={`text-sm ${theme.textSecondary}`}>Goals Achieved</span></div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{overview?.goals_achieved || 0}</p>
              </div>
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <div className="flex items-center gap-2 mb-2"><TrendingUp className={`w-5 h-5 ${theme.textSecondary}`} /><span className={`text-sm ${theme.textSecondary}`}>Best Streak</span></div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{overview?.longest_streak || 0} days</p>
              </div>
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <div className="flex items-center gap-2 mb-2"><Clock className={`w-5 h-5 ${theme.textSecondary}`} /><span className={`text-sm ${theme.textSecondary}`}>Total Minutes</span></div>
                <p className={`text-2xl font-bold ${theme.textPrimary}`}>{overview?.total_duration || 0}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsPage;
